import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { PageTopNav } from "@/components/nav/PageTopNav";

export const OrgMessageDetail: React.FC = () => {
  const navigate = useNavigate();
  const { orgId, messageId } = useParams<{ orgId: string; messageId: string }>();

  const {
    data: message,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["orgMessageDetail", orgId, messageId],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}/contactRequest/${messageId}`);
      if (!res.ok) throw new Error("Failed to fetch message detail");
      // API returns an array, get the first item
      const arr = await res.json();
      return Array.isArray(arr) ? arr[0] : arr;
    },
    enabled: !!orgId && !!messageId,
  });

  return (
    <div className="container mx-auto max-w-xl p-4">
      <PageTopNav pageName="Message Detail" rightItem={
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
      } />
      {isLoading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load message. Please try again.
        </div>
      ) : !message ? (
        <div className="text-center p-8 text-muted-foreground">Message not found.</div>
      ) : (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-lg">{message.subject || "Contact Request"}</CardTitle>
            <CardDescription>
              <span className="font-medium">From:</span> {message.senderName || message.email || "Unknown"}
              <br />
              <span className="font-medium">Date:</span> {message.createdAt ? new Date(message.createdAt).toLocaleString() : "-"}
              <br />
              <span className="font-medium">Status:</span> {message.open === false ? "Closed" : "Open"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-2">
              <span className="font-medium">Message:</span>
              <div className="whitespace-pre-wrap mt-1 bg-muted/50 rounded p-2 text-sm">
                {message.message || message.body || "No message content."}
              </div>
            </div>
            {message.closeMessage && (
              <div className="mt-4">
                <span className="font-medium">Close Message:</span>
                <div className="whitespace-pre-wrap mt-1 bg-muted/50 rounded p-2 text-sm">
                  {message.closeMessage}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
